import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCheck } from "@fortawesome/free-solid-svg-icons";

// Stable per-instance id source (React 17 has no useId) for label/trigger association.
let multiSelectSeq = 0;

// Multi-select dropdown for the Advanced Selection filter (Standards, Learning
// Components). Visual language matches SelectDropdown in FilterDropdowns.js, but each
// row is a toggleable checkbox and the menu stays open across selections.
//
// Props:
//   label       — visible field label (e.g. "Standards")
//   allNoun     — noun for the "all" state (e.g. "Standards", "Learning Components")
//   values      — array of currently-selected option values ([] === all)
//   options     — [{ value, label, count?, disabled? }]
//   onChange    — (nextValues) => void
//   disabled    — disables the whole control
//
// Empty `values` means "all" (no filter), matching the backend's all-empty === no
// filter rule; the trigger then reads "All {option count} {allNoun}".
export const MultiSelectDropdown = ({ label, allNoun, values = [], options = [], onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const labelIdRef = useRef(null);
  if (labelIdRef.current === null) labelIdRef.current = `multi-select-label-${++multiSelectSeq}`;
  const labelId = labelIdRef.current;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && activeIndex >= 0) itemRefs.current[activeIndex]?.focus();
  }, [isOpen, activeIndex]);

  // Stored values and option values can differ in case (standard codes arrive from
  // more than one source), and a stored value may no longer be offered at all. Only
  // values that match an option count as selected, so the trigger, the header and
  // the checked rows can never disagree.
  const same = (a, b) => String(a).toUpperCase() === String(b).toUpperCase();
  const isSelected = (optValue) => values.some((v) => same(v, optValue));
  const selectedOptions = options.filter((o) => isSelected(o.value));
  const selectedCount = selectedOptions.length;
  // Every option checked filters nothing, exactly like none checked, so both read
  // "All N" — the rows still show the checks and Clear All stays available.
  const allSelected = selectedCount > 0 && selectedCount === options.length;
  const allText = `${options.length} ${allNoun}`;
  const displayText =
    selectedCount === 0 || allSelected
      ? `All ${allText}`
      : selectedCount === 1
        ? selectedOptions[0].label
        : `${selectedCount} Selected`;

  // Keyboard opens move focus (and its visible highlight) to the first option; mouse
  // opens leave focus on the trigger so the menu renders with no row pre-highlighted —
  // otherwise the same click shows a highlighted row on some surfaces and not others,
  // depending on the browser's current input modality.
  const openMenu = ({ focusFirst = true } = {}) => {
    const firstEnabled = options.findIndex((o) => !o.disabled);
    setActiveIndex(focusFirst ? (firstEnabled >= 0 ? firstEnabled : 0) : -1);
    setIsOpen(true);
  };
  const closeMenu = ({ returnFocus = true } = {}) => {
    setIsOpen(false);
    setActiveIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  };

  // Checking the last unchecked option means "everything", which is the unfiltered
  // state — so it collapses to Clear All rather than an explicit list of every id.
  // Disabled options count too: picking the only live option is still a choice.
  const toggleValue = (optValue) => {
    const next = isSelected(optValue)
      ? values.filter((v) => !same(v, optValue))
      : [...values, optValue];
    const coversAll = options.every((o) => next.some((v) => same(v, o.value)));
    onChange(coversAll ? [] : next);
  };

  const moveActive = (from, dir) => {
    let i = from;
    for (let step = 0; step < options.length; step += 1) {
      i += dir;
      if (i < 0 || i > options.length - 1) return from;
      if (!options[i].disabled) return i;
    }
    return from;
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      isOpen ? setActiveIndex((i) => moveActive(i, 1)) : openMenu();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openMenu();
    }
  };

  const handleItemKeyDown = (e, i) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(moveActive(i, 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(moveActive(i, -1)); }
    else if (e.key === "Home") { e.preventDefault(); setActiveIndex(moveActive(-1, 1)); }
    else if (e.key === "End") { e.preventDefault(); setActiveIndex(moveActive(options.length, -1)); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!options[i].disabled) toggleValue(options[i].value); }
    else if (e.key === "Escape") { e.preventDefault(); closeMenu(); }
    else if (e.key === "Tab") { closeMenu({ returnFocus: false }); }
  };

  return (
    <Container ref={ref}>
      {label && <FieldLabel id={labelId}>{label}</FieldLabel>}
      <Trigger
        ref={triggerRef}
        type="button"
        $disabled={disabled}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : `${allNoun}: ${displayText}`}
        onClick={() => (disabled ? null : isOpen ? closeMenu({ returnFocus: false }) : openMenu({ focusFirst: false }))}
        onKeyDown={handleTriggerKeyDown}
      >
        <TriggerText>{displayText}</TriggerText>
        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "16px", color: colors.nightlyWoods500 }} />
      </Trigger>
      {isOpen && (
        <Menu>
          <MenuHeader>
            <span>
              {selectedCount === 0 || allSelected ? allText : `${selectedCount} Selected`}
            </span>
            {selectedCount > 0 && (
              <ClearAllButton type="button" onClick={() => onChange([])}>
                Clear All
              </ClearAllButton>
            )}
          </MenuHeader>
          <OptionList role="listbox" aria-multiselectable="true" aria-label={label || allNoun} tabIndex={-1}>
          {options.length === 0 ? (
            <EmptyRow>No options available.</EmptyRow>
          ) : (
            options.map((opt, i) => {
              const checked = isSelected(opt.value);
              return (
                <Item
                  key={opt.value}
                  ref={(el) => (itemRefs.current[i] = el)}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  aria-disabled={opt.disabled || undefined}
                  tabIndex={i === activeIndex ? 0 : -1}
                  $disabled={opt.disabled}
                  onClick={() => (opt.disabled ? null : toggleValue(opt.value))}
                  onKeyDown={(e) => handleItemKeyDown(e, i)}
                >
                  <Box $checked={checked} $disabled={opt.disabled} aria-hidden>
                    {checked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: "12px", color: "white" }} />}
                  </Box>
                  <ItemLabel>
                    {typeof opt.count === "number" ? `${opt.label} (${opt.count})` : opt.label}
                  </ItemLabel>
                </Item>
              );
            })
          )}
          </OptionList>
        </Menu>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const FieldLabel = styled.label`
  display: block;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.nightlyWoods500};
  margin-bottom: 6px;
  white-space: nowrap;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background: ${(p) => (p.$disabled ? colors.neutral100 : "white")};
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  min-height: 40px;
  gap: 8px;
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${(p) => (p.$disabled ? colors.blue300 : colors.blue400)};
  }
  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 1px;
  }
`;

const TriggerText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.nightlyWoods500};
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const Menu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 350px;
  max-width: calc(100vw - 32px);
  margin-top: 4px;
  background: white;
  border: 1px solid #bab9f5;
  border-radius: 6px;
  box-shadow: 0px 4px 12px rgba(14, 26, 49, 0.12);
  z-index: 100;
  max-height: 480px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  padding: 16px;
  border-bottom: 1px solid #acafad;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.blue700};

  &:hover { text-decoration: underline; }
  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const OptionList = styled.div`
  overflow-y: auto;
  padding: 4px 0;

  scrollbar-width: thin;
  scrollbar-color: #b9b7b3 transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #b9b7b3; border-radius: 2px; }
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: none;
  padding: 8px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${(p) => (p.$disabled ? colors.milkGlass950 : colors.nightlyWoods500)};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  line-height: 24px;
  background: transparent;
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};

  &:hover {
    background: ${(p) => (p.$disabled ? "transparent" : colors.neutral100)};
  }
  &:focus-visible {
    outline: none;
    background: ${colors.neutral100};
    box-shadow: inset 2px 0 0 ${colors.blue500};
  }
`;

const Box = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid ${(p) => (p.$checked ? colors.green500 : colors.nightlyWoods500)};
  background: ${(p) => (p.$checked ? colors.green500 : "white")};
`;

const ItemLabel = styled.span`
  flex: 1;
  min-width: 0;
  line-height: 20px;
`;

const EmptyRow = styled.div`
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.milkGlass950};
`;
